import React, { useState, useRef, useEffect } from 'react';
import { verifyEmailCode, resendVerificationCode } from '../../services/auth';
import LottieAnimation from './LottieAnimation';
import ProxyImage from '../ProxyImage';
import './Auth.css';

interface VerifyCodeProps {
  email: string;
  onSuccess: () => void;
}

const VerifyCode: React.FC<VerifyCodeProps> = ({ email, onSuccess }) => {
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus en el primer input
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    // Solo permitir números
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setErrors({});
    setMessage(null);

    // Auto-avanzar al siguiente input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Si se completó el código, verificar automáticamente
    if (newCode.every(digit => digit !== '') && newCode.join('').length === 6) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Retroceder al input anterior con Backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      const newCode = [...code];
      for (let i = 0; i < 6; i++) {
        newCode[i] = digits[i] || '';
      }
      setCode(newCode);

      // Verificar automáticamente
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (codeToVerify?: string) => {
    const codeString = codeToVerify || code.join('');

    if (codeString.length !== 6) {
      setErrors({ general: 'Por favor, ingresa el código completo de 6 dígitos' });
      return;
    }

    if (!/^\d{6}$/.test(codeString)) {
      setErrors({ general: 'El código debe contener solo números' });
      return;
    }

    setLoading(true);
    setErrors({});
    setMessage(null);

    try {
      const result = await verifyEmailCode(email, codeString);

      if (result.success) {
        setMessage('Email verificado exitosamente');
        setTimeout(() => {
          onSuccess();
        }, 1000);
      } else {
        setErrors({ general: result.error || 'Código inválido. Por favor, verifica e intenta nuevamente.' });
        // Limpiar el código para que el usuario pueda ingresarlo de nuevo
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      setErrors({ general: 'Error de conexión. Por favor, intenta nuevamente.' });
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleVerify();
  };

  return (
    <div className="auth-container">
      <div className="auth-wrapper">
        <div className="auth-lottie-column">
          <div className="auth-logo">
            <a 
              href="https://smartcityexposantiagodelestero.com/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <ProxyImage 
                src="https://smartcityexposantiagodelestero.com/wp-content/uploads/2025/03/Logo-SCESDE26-Negro-e1751508867641-1536x213.png" 
                alt="Smart City Expo Santiago del Estero" 
              />
            </a>
          </div>
          <LottieAnimation />
          <div className="auth-partners-image">
            <ProxyImage 
              src="https://smartcityexposantiagodelestero.com/wp-content/uploads/2026/01/Instituciones-Colaboradoras-Web-scaled-e1767776957958.png" 
              alt="Instituciones Colaboradoras" 
            />
          </div>
        </div>
        <div className="auth-form-column">
          <div className="auth-card">
            <div className="auth-logo-mobile">
              <a 
                href="https://smartcityexposantiagodelestero.com/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <ProxyImage 
                  src="https://smartcityexposantiagodelestero.com/wp-content/uploads/2025/03/Logo-SCESDE26-Negro-e1751508867641-1536x213.png" 
                  alt="Smart City Expo Santiago del Estero" 
                />
              </a>
            </div>
        <h2 className="auth-title">Verificar Email</h2>
        <p className="auth-subtitle">
          Ingresa el código de 6 dígitos que enviamos a<br />
          <strong>{email}</strong>
        </p>

        {message && (
          <div className="auth-message success">
            {message}
          </div>
        )}

        {errors.general && (
          <div className="auth-message error">
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '12px', 
            marginBottom: '20px' 
          }}>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="code-input"
                disabled={loading}
                style={{
                  width: '50px',
                  height: '60px',
                  fontSize: '24px',
                  textAlign: 'center',
                  border: errors.general ? '2px solid #ef4444' : '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 600,
                  outline: 'none',
                  transition: 'all 0.2s',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#CE2B15';
                  e.target.style.boxShadow = '0 0 0 3px rgba(206, 43, 21, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.general ? '#ef4444' : '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              />
            ))}
          </div>

          <button 
            type="submit" 
            className="auth-button"
            disabled={loading || code.join('').length !== 6}
          >
            {loading ? 'Verificando...' : 'Verificar Código'}
          </button>
        </form>

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          {resendMessage && (
            <div className={resendMessage.includes('exitosamente') ? 'auth-message success' : 'auth-message error'}>
              {resendMessage}
            </div>
          )}
          <button 
            type="button" 
            onClick={async () => {
              setResending(true);
              setResendMessage(null);
              setErrors({});

              try {
                const result = await resendVerificationCode(email);
                if (result.success) {
                  setResendMessage('Código reenviado exitosamente. Revisa tu correo.');
                  // Limpiar el código actual
                  setCode(['', '', '', '', '', '']);
                  inputRefs.current[0]?.focus();
                } else {
                  setResendMessage(result.error || 'Error al reenviar código');
                }
              } catch (error) {
                setResendMessage('Error de conexión. Por favor, intenta nuevamente.');
              } finally {
                setResending(false);
              }
            }}
            className="auth-link"
            disabled={loading || resending}
          >
            {resending ? 'Reenviando...' : 'Reenviar código'}
          </button>
        </div>

        <div className="auth-switch">
          <p>¿No recibiste el código? Verifica tu carpeta de spam</p>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyCode;
